import {useTranslation} from "react-i18next";
import {useContext, useEffect, useState} from "react";
import {Button, Col, Divider, Row} from "antd";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {useNavigate, useParams} from "react-router-dom";
import {getTranslations} from "../../../utils/helpers/translations";
import {IUpdateAchievement} from "../../../models/achievement/request";
import {execute} from "../../../utils/helpers/execute";
import styles from "../../trademark/trademark-form/style.module.scss";
import Spin from "../../general/antd/spin";
import {languages} from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import AchievementContextProvider from "../../../context/achievement/provider";
import AchievementContext from "../../../context/achievement/context";
import {ICity} from "../../../models/city/response";
import EndPoints from "../../../services/end-points";
import AppContext from "../../../context/app/context";
import FormItem from "../../general/form-item";
import ImageUploader from "../../form-components/image-uploader";
import {MediumFor} from "../../../models/medium/enum";
import OldImage from "../../form-components/old-image";

const AchievementForm = () => {     
    const {t} = useTranslation();
    const {details, actions, loading} = useContext(AchievementContext);
    const {id} = useParams();
    const navigate = useNavigate();
    const [cities, setCities] = useState<ICity[]>([]);
    const [citiesLoading, setCitiesLoading] = useState(true);
    const { direction } = useContext(AppContext);

    const methods = useForm<any>({
        defaultValues: {
            videoLink: details?.videoLink || "",
            title: getTranslations(details?.translations?.title) || {},
            description: getTranslations(details?.translations?.description) || {},
            content: getTranslations(details?.translations?.content) || {},
            images: details?.images?.map(img => img.id) || [],
            date: details?.date || "",
            cityId: details?.city?.id,
            donationValue: details?.donationValue || 0,
            beneficiariesNumber: details?.beneficiariesNumber || 0,
            imageId: details?.image?.id ?? undefined,
        } as IUpdateAchievement,
    });

    const {
        handleSubmit,
        reset,
        control,
        setValue,
        watch,
        formState: {errors},
    } = methods;

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const images = watch("images") || [];
    useEffect(() => {
        const getCities = async () => {
            try {
                const { data } = await EndPoints.city.getAllCities({
                    light: true,
                    perPage: -1,
                });
                setCities(data.data);
                setCitiesLoading(false);
            } catch (error) {
            } finally {
                setCitiesLoading(false);
            }
        };
        getCities();
    }, [direction]);
    useEffect(() => {
        reset({
            videoLink: details?.videoLink || "",
            title: getTranslations(details?.translations?.title) || {},
            description: getTranslations(details?.translations?.description) || {},
            content: getTranslations(details?.translations?.content) || {},
            images: details?.images?.map(img => img.id) || [],
            date: details?.date || "",
            donationValue: details?.donationValue || 0,
            beneficiariesNumber: details?.beneficiariesNumber || 0,
            cityId:details?.city?.id,
            imageId: details?.image?.id ?? undefined,

        } as IUpdateAchievement);
    }, [details]);

    useEffect(() => {
        id && actions.getDetails(Number(id));
    }, [id]);
// functions edit

    const handleImageChange = (id: number) => {
        const newImages = [...images];
        if (newImages.length <= currentImageIndex) {
            newImages.length = currentImageIndex + 1;
        }
        newImages[currentImageIndex] = id;
        setValue("images", newImages);
    };

    const handleAddNewImage = () => {
        const newImages = [...images, null];
        setValue("images", newImages);
        setCurrentImageIndex(newImages.length - 1);
    };

    const handleRemoveImage = () => {
        const newImages = [...images];
        newImages.splice(currentImageIndex, 1);
        setValue("images", newImages);
        setCurrentImageIndex(Math.max(0, currentImageIndex - 1));
    };
//

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const onSubmit = async (data: any) => {
        console.log(data);
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                details
                    ? await actions.updateAchievement(details?.id, data)
                    : await actions.createAchievement(data);
                navigate(-1);
            },
            fallback: (error) => {
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <span className={styles.title}>
                        {details ? t("update_achievement") : t("add_achievement")}
                    </span>
                    <Divider className={styles.divider}/>
                    {loading.includes("details") ? (
                        <div style={{height: "60vh"}}>
                            <Spin/>
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Row gutter={[16, 8]}>
                                <Col span={24}>
                                    <FieldBuilder
                                        name="videoLink"
                                        label={t("video_link")}
                                        input={{type: "text"}}
                                        rules={{required: true}}
                                    />
                                </Col>

                                <Col span={12}>
                                    <FieldBuilder
                                        name="date"
                                        label={t("date")}
                                        input={{ type: "date-picker", format: "YYYY-MM-DD" }}
                                        rules={{required: true}}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="cityId"
                                        label={`${t("city")}`}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            loading: citiesLoading,
                                            allowSearch: true,
                                            options: cities?.map((city, index) => {
                                                return {
                                                    key: index,
                                                    label: city.name,
                                                    value: city.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>

                                <Col span={12}>
                                    <FieldBuilder
                                        name="donationValue"
                                        label={t("donation_value")}
                                        input={{
                                            type: "number",
                                        }}
                                        rules={{required: true}}
                                    />
                                </Col>

                                <Col span={12}>
                                    <FieldBuilder
                                        name="beneficiariesNumber"
                                        label={t("number_of_beneficiaries")}
                                        input={{
                                            type: "number",
                                        }}
                                        rules={{required: true}}
                                    />
                                </Col>


                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`title[${lang?.code}]`}
                                            label={`${t("title")} (${lang?.name})`}
                                            input={{type: "text"}}
                                            rules={{required: true}}
                                        />
                                    </Col>
                                ))}

                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`description[${lang?.code}]`}
                                            label={`${t("description")} (${lang?.name})`}
                                            input={{type: "text-area"}}
                                            rules={{required: true}}
                                        />
                                    </Col>
                                ))}

                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`content[${lang?.code}]`}
                                            label={`${t("content")} (${lang?.name})`}
                                            input={{type: "text-area"}}
                                            rules={{required: true}}
                                        />
                                    </Col>
                                ))}

                                {/* Image Carousel Uploader */}
                                {/*<Col span={24}>*/}
                                {/*    <FormItem label={t("images")}>*/}
                                {/*        <div className="image-carousel-uploader">*/}
                                {/*            <div className="navigation-arrows">*/}
                                {/*                <Button*/}
                                {/*                    icon={<LeftOutlined/>}*/}
                                {/*                    onClick={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))}*/}
                                {/*                    disabled={currentImageIndex === 0}*/}
                                {/*                />*/}
                                {/*                <span>*/}
                                {/*                    Image {imageIds.length > 0 ? currentImageIndex + 1 : 0} of {imageIds.length}*/}
                                {/*                </span>*/}
                                {/*                <Button*/}
                                {/*                    icon={<RightOutlined/>}*/}
                                {/*                    onClick={() => setCurrentImageIndex(Math.min(imageIds.length, currentImageIndex + 1))}*/}
                                {/*                    disabled={currentImageIndex === imageIds.length - 1}*/}
                                {/*                />*/}
                                {/*            </div>*/}

                                {/*            <Controller*/}
                                {/*                name={`imageIds[${currentImageIndex}]`}*/}
                                {/*                render={({field: {ref, ...field}}) => (*/}
                                {/*                    <div className="uploader-container">*/}
                                {/*                        <ImageUploader*/}
                                {/*                            {...field}*/}
                                {/*                            type="DRAGGER"*/}
                                {/*                            onChange={(response) => handleImageChange(response?.id)}*/}
                                {/*                            imageFor={MediumFor.UserProfile}*/}
                                {/*                        />*/}
                                {/*                        {imageIds[currentImageIndex] && (*/}
                                {/*                            <Button*/}
                                {/*                                danger*/}
                                {/*                                onClick={handleRemoveImage}*/}
                                {/*                                className="remove-button"*/}
                                {/*                            >*/}
                                {/*                                Remove Image*/}
                                {/*                            </Button>*/}
                                {/*                        )}*/}
                                {/*                    </div>*/}
                                {/*                )}*/}
                                {/*            />*/}

                                {/*            <Button*/}
                                {/*                type="dashed"*/}
                                {/*                onClick={() => setCurrentImageIndex(imageIds.length)}*/}
                                {/*                className="add-new-button"*/}
                                {/*            >*/}
                                {/*                Add New Image*/}
                                {/*            </Button>*/}
                                {/*        </div>*/}
                                {/*    </FormItem>*/}
                                {/*</Col>*/}

                                {/* Preview existing images */}
                                {/*{details?.images?.map((image, index) => (*/}
                                {/*    <Col span={8} key={index}>*/}
                                {/*        <OldImage src={image.url}/>*/}
                                {/*    </Col>*/}
                                {/*))}*/}
                                <Col span={24}>
                                    <FormItem label={t("image")} required>
                                        <Controller
                                            name="imageId"
                                            rules={{
                                                required: {
                                                    value: true,
                                                    message: `${t("field_is_required_message")}`,
                                                },
                                            }}
                                            render={({ field: { ref, ...field } }) => {
                                                return (
                                                    <ImageUploader
                                                        {...field}
                                                        type="DRAGGER"
                                                        onChange={(response) => {
                                                            // setfileId(response);
                                                            field.onChange(response?.id);
                                                        }}
                                                        imageFor={MediumFor.UserProfile}
                                                    />
                                                );
                                            }}
                                        />
                                    </FormItem>
                                </Col>
                                {details?.image && <OldImage src={details?.image?.url} />}
                            </Row>
                        </div>
                    )}
                </div>
                <div>
                    <div className={styles.formFooter}>
                        <Button onClick={() => navigate(-1)}>
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const AchievementFormWrapper = () => {
    return (
        <AchievementContextProvider>
            <AchievementForm/>
        </AchievementContextProvider>
    );
};

export default AchievementFormWrapper;